import React, { useState, useMemo } from 'react';
import {
  Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Chip, Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const alignConfig = {
  BULLISH: { color: '#059669', bg: '#ecfdf5' },
  BEARISH: { color: '#dc2626', bg: '#fef2f2' },
  NEUTRAL: { color: '#6b7280', bg: '#f3f4f6' },
};

function recColor(rec) {
  const r = (rec || '').toUpperCase();
  if (r.includes('STRONG BUY'))  return { color: '#047857', bg: '#d1fae5' };
  if (r.includes('ACCUMULATE'))  return { color: '#059669', bg: '#ecfdf5' };
  if (r.includes('BUY'))         return { color: '#16a34a', bg: '#f0fdf4' };
  if (r.includes('CAUTION'))     return { color: '#d97706', bg: '#fffbeb' };
  if (r.includes('WAIT'))        return { color: '#9ca3af', bg: '#f3f4f6' };
  if (r.includes('AVOID'))       return { color: '#ea580c', bg: '#fff7ed' };
  if (r.includes('SELL'))        return { color: '#dc2626', bg: '#fef2f2' };
  return { color: '#6b7280', bg: '#f3f4f6' };  // HOLD
}

const columnDefs = [
  { id: 'sector_name', tKey: 'sector', sortable: true, minW: 130 },
  { id: 'combined_score', tKey: 'score', sortable: true, minW: 60, align: 'center' },
  { id: 'impulse_score_0_100', tKey: 'impulse', sortable: true, minW: 60, align: 'center' },
  { id: 'rs_rank', tKey: 'rsRank', sortable: true, minW: 55, align: 'center' },
  { id: 'rs_3m', tKey: 'rs3m', sortable: true, minW: 55, align: 'center' },
  { id: 'rs_6m', tKey: 'rs6m', sortable: true, minW: 55, align: 'center' },
  { id: 'dist_from_ma20_pct', tKey: 'ma20', sortable: true, minW: 60, align: 'center' },
  { id: 'dist_from_ma50_pct', tKey: 'ma50', sortable: true, minW: 60, align: 'center' },
  { id: 'ma_alignment', tKey: 'trend', sortable: true, minW: 75, align: 'center' },
  { id: 'ret_3m_pct', tKey: 'ret3m', sortable: true, minW: 60, align: 'center' },
  { id: 'recommendation', tKey: 'recommendation', sortable: false, minW: 90, align: 'center' },
];

function scoreLabel(score, t) {
  if (score >= 70) return t('marketBreadth.table.scoreStrong');
  if (score >= 50) return t('marketBreadth.table.scoreOkay');
  if (score >= 30) return t('marketBreadth.table.scoreWeak');
  return t('marketBreadth.table.scorePoor');
}

function semanticTone(kind, value) {
  if (kind === 'score') {
    if (value >= 70) return { color: '#047857', bg: '#d1fae5' };
    if (value >= 50) return { color: '#2563eb', bg: '#eff6ff' };
    if (value >= 30) return { color: '#b45309', bg: '#fffbeb' };
    return { color: '#dc2626', bg: '#fef2f2' };
  }
  if (kind === 'impulse') {
    if (value >= 70) return { color: '#059669', bg: '#ecfdf5' };
    if (value >= 50) return { color: '#0284c7', bg: '#f0f9ff' };
    if (value >= 30) return { color: '#d97706', bg: '#fff7ed' };
    return { color: '#dc2626', bg: '#fef2f2' };
  }
  if (kind === 'rank') {
    if (value <= 3) return { color: '#1d4ed8', bg: '#dbeafe' };
    if (value <= 6) return { color: '#475569', bg: '#f1f5f9' };
    return { color: '#92400e', bg: '#fef3c7' };
  }
  if (kind === 'relativeStrength') {
    if (value >= 3) return { color: '#047857', bg: '#d1fae5' };
    if (value >= 0) return { color: '#0f766e', bg: '#ecfeff' };
    if (value >= -3) return { color: '#b45309', bg: '#fff7ed' };
    return { color: '#dc2626', bg: '#fef2f2' };
  }
  if (kind === 'ma') {
    if (value >= 0) return { color: '#0f766e', bg: '#ecfeff' };
    return { color: '#dc2626', bg: '#fef2f2' };
  }
  if (kind === 'return') {
    if (value >= 5) return { color: '#047857', bg: '#d1fae5' };
    if (value >= 0) return { color: '#059669', bg: '#ecfdf5' };
    if (value >= -5) return { color: '#b45309', bg: '#fffbeb' };
    return { color: '#dc2626', bg: '#fef2f2' };
  }
  return { color: '#64748b', bg: '#f8fafc' };
}

function HintBadge({ label, tone }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.7,
        py: 0.18,
        borderRadius: 999,
        bgcolor: tone.bg,
        color: tone.color,
        fontSize: '0.62rem',
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

function impulseLabel(value, t) {
  if (value >= 70) return t('marketBreadth.table.impulseStrong');
  if (value >= 50) return t('marketBreadth.table.impulseOkay');
  if (value >= 30) return t('marketBreadth.table.impulseWeak');
  return t('marketBreadth.table.impulsePoor');
}

function rankLabel(rank, t) {
  if (rank == null) return '-';
  if (rank <= 3) return t('marketBreadth.table.rankTop');
  if (rank <= 6) return t('marketBreadth.table.rankMid');
  return t('marketBreadth.table.rankLow');
}

function relativeStrengthLabel(value, t) {
  if (value == null) return '-';
  if (value >= 3) return t('marketBreadth.table.rsVeryPositive');
  if (value >= 0) return t('marketBreadth.table.rsPositive');
  if (value >= -3) return t('marketBreadth.table.rsSlightNegative');
  return t('marketBreadth.table.rsNegative');
}

function movingAverageLabel(value, t, shortKey, belowKey) {
  if (value == null) return '-';
  return value >= 0 ? t(shortKey) : t(belowKey);
}

function returnLabel(value, t) {
  if (value == null) return '-';
  if (value >= 5) return t('marketBreadth.table.retStrong');
  if (value >= 0) return t('marketBreadth.table.retPositive');
  if (value >= -5) return t('marketBreadth.table.retWeak');
  return t('marketBreadth.table.retPoor');
}

function SectorAnalysisTable({ sectorData, onSectorClick }) {
  const { t, i18n } = useTranslation();
  const [orderBy, setOrderBy] = useState('combined_score');
  const [order, setOrder] = useState('desc');

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder('desc');
    }
  };

  const sorted = useMemo(() => {
    if (!sectorData) return [];
    return [...sectorData].sort((a, b) => {
      let av = a[orderBy], bv = b[orderBy];
      if (orderBy === 'sector_name') {
        const lang = i18n.language === 'vi' ? 'vi' : 'en';
        av = a.sector_name?.[lang] || a.sector_name?.en || '';
        bv = b.sector_name?.[lang] || b.sector_name?.en || '';
        return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (orderBy === 'ma_alignment') {
        av = av || ''; bv = bv || '';
        return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      av = Number(av) || 0; bv = Number(bv) || 0;
      return order === 'asc' ? av - bv : bv - av;
    });
  }, [sectorData, orderBy, order, i18n.language]);

  const lang = i18n.language === 'vi' ? 'vi' : 'en';

  return (
    <Box sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2.5,
      bgcolor: 'background.paper', overflow: 'hidden',
    }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
          {t('marketBreadth.table.title')}
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: '0.72rem', color: 'text.secondary' }}>
          {t('marketBreadth.table.description')}
        </Typography>
      </Box>

      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columnDefs.map(col => {
                const label = t(`marketBreadth.table.${col.tKey}`, col.tKey);
                return (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  sx={{
                    fontWeight: 700, fontSize: '0.71rem', py: 1, px: 1.25,
                    minWidth: col.minW, whiteSpace: 'nowrap', bgcolor: 'background.paper',
                    borderBottom: '2px solid', borderColor: 'divider',
                    textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary',
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'desc'}
                      onClick={() => handleSort(col.id)}
                      sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 } }}
                    >
                      {label}
                    </TableSortLabel>
                  ) : label}
                </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {sorted.map((row, idx) => {
              const name = row.sector_name?.[lang] || row.sector_name?.en || '-';
              const score = row.combined_score ?? 0;
              const impulse = row.impulse_score_0_100 ?? 0;
              const alignKey = row.ma_alignment || 'NEUTRAL';
              const align = alignConfig[alignKey] || alignConfig.NEUTRAL;
              const rec = recColor(row.recommendation);
              const recLabel = t(`marketBreadth.recommendation.${row.recommendation}`, row.recommendation);
              const trendLabel = t(`marketBreadth.trend.${alignKey}`, alignKey);
              const shortMa20Label = movingAverageLabel(row.dist_from_ma20_pct, t, 'marketBreadth.table.ma20Above', 'marketBreadth.table.ma20Below');
              const shortMa50Label = movingAverageLabel(row.dist_from_ma50_pct, t, 'marketBreadth.table.ma50Above', 'marketBreadth.table.ma50Below');

              return (
                <TableRow
                  key={idx}
                  hover
                  onClick={() => onSectorClick?.(row.sector_name?.en)}
                  sx={{
                    cursor: onSectorClick ? 'pointer' : 'default',
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ py: 1, px: 1.25 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', color: '#1e293b' }}>
                      {name}
                    </Typography>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <HintBadge label={scoreLabel(score, t)} tone={semanticTone('score', score)} />
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <HintBadge label={impulseLabel(impulse, t)} tone={semanticTone('impulse', impulse)} />
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title={t('marketBreadth.table.rsRankHint')}>
                        <Box>
                          <HintBadge label={rankLabel(row.rs_rank, t)} tone={semanticTone('rank', row.rs_rank ?? 999)} />
                        </Box>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <HintBadge label={relativeStrengthLabel(row.rs_3m, t)} tone={semanticTone('relativeStrength', row.rs_3m ?? 0)} />
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <HintBadge label={relativeStrengthLabel(row.rs_6m, t)} tone={semanticTone('relativeStrength', row.rs_6m ?? 0)} />
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <HintBadge label={shortMa20Label} tone={semanticTone('ma', row.dist_from_ma20_pct ?? 0)} />
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <HintBadge label={shortMa50Label} tone={semanticTone('ma', row.dist_from_ma50_pct ?? 0)} />
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Chip
                      label={trendLabel}
                      size="small"
                      sx={{
                        height: 22, fontSize: '0.68rem', fontWeight: 700,
                        bgcolor: align.bg, color: align.color,
                        border: 'none', borderRadius: 1,
                      }}
                    />
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <HintBadge label={returnLabel(row.ret_3m_pct, t)} tone={semanticTone('return', row.ret_3m_pct ?? 0)} />
                    </Box>
                  </TableCell>

                  <TableCell align="center" sx={{ py: 1, px: 1 }}>
                    <Chip
                      label={recLabel || '-'}
                      size="small"
                      sx={{
                        height: 22, fontSize: '0.65rem', fontWeight: 700,
                        bgcolor: rec.bg, color: rec.color,
                        border: 'none', borderRadius: 1,
                        maxWidth: 100,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default SectorAnalysisTable;
